import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useQuery, useMutation } from '@tanstack/react-query';
import orientationService from '../../../api/services/orientationService';
import departmentRoleService from '../../../api/services/departmentRoleService';
import hrService from '../../../api/services/hrService';
import fieldSalesService from '../../../api/crm/field-sales/fieldSalesService';
import { API_BASE_URL } from '../../../api/config';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import {
  ArrowLeft,
  Calendar,
  Send,
  Plus,
  X,
  Search,
  Check,
  ChevronDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';

const CreateOrientation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { hasPermission, canCreate } = usePermissions();
  const { showSuccess, showError } = useCustomAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingOrientation, setIsLoadingOrientation] = useState(false);
  const [errors, setErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [trainerSearchTerm, setTrainerSearchTerm] = useState('');
  const [debouncedTrainerSearch, setDebouncedTrainerSearch] = useState('');
  const [showTrainerDropdown, setShowTrainerDropdown] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [selectedTrainerData, setSelectedTrainerData] = useState(null);
  const trainerDropdownRef = useRef(null);
  const departmentDropdownRef = useRef(null);
  const agendaIdCounterRef = useRef(1);
  const [selectedLocationData, setSelectedLocationData] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: '',
    location: '',
    date: '',
    time: '',
    duration: '',
    capacity: '',
    trainer: [],
    department: '',
    agenda: [],
    materials: [],
    requirements: []
  });

  // Pre-compute max future date (1 year from today) for date input constraints
  const maxFutureDate = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  // Permission-based access control (Dynamic RBAC)
  const canCreateOrientation = () => {
    return canCreate('onboarding');
  };

  // Fetch departments using React Query
  const { data: departmentsData, isLoading: loadingDepartments } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {

        const result = await departmentRoleService.getDepartments();



        let departmentsData = [];

        if (Array.isArray(result)) {
          departmentsData = result;

        } else if (result && result.data) {
          departmentsData = Array.isArray(result.data) ? result.data : result.data.departments || [];

        } else if (result && result.responseCode === 200) {
          departmentsData = result.data?.departments || result.data || [];

        } else {

          departmentsData = [];
        }



        const departmentNames = departmentsData.map(dept => {
          if (typeof dept === 'string') {
            return dept;
          } else if (dept && typeof dept === 'object') {
            const name = dept.name || dept.departmentName || dept.title || dept.department || dept._id || '';

            return name;
          }
          return '';
        }).filter(Boolean);


        return departmentNames;
      } catch (err) {
        return [];
      }
    },
    staleTime: 0,
    retry: 2,
  });

  const departments = departmentsData || [];

  // Fetch orientation data if editing (ID exists)
  useEffect(() => {
    const loadOrientationForEdit = async () => {
      if (!id) return;

      try {
        setIsLoadingOrientation(true);

        const result = await orientationService.getOrientationById(id);

        if (result && result.responseCode === 200 && result.data) {
          const orientationData = result.data.orientation;

          // Ensure all agenda items have an id field
          const agendaWithIds = (orientationData.agenda || []).map((item, index) => ({
            ...item,
            id: item.id || item._id || agendaIdCounterRef.current++
          }));

          setFormData({
            title: orientationData.title || '',
            description: orientationData.description || '',
            type: orientationData.type || 'in_person',
            location: orientationData.location || (orientationData.type === 'virtual' ? '' : null),
            date: orientationData.date ? new Date(orientationData.date).toISOString().split('T')[0] : '',
            time: orientationData.time || '',
            duration: orientationData.duration || 4,
            capacity: orientationData.capacity || 20,
            trainer: Array.isArray(orientationData.trainer) ? orientationData.trainer : (orientationData.trainer ? [orientationData.trainer] : []),
            department: orientationData.department || '',
            agenda: agendaWithIds,
            materials: orientationData.materials || [],
            requirements: orientationData.requirements || []
          });

          // Set selected trainers directly if available (preferred)
          if (orientationData.trainerData && Array.isArray(orientationData.trainerData) && orientationData.trainerData.length > 0) {
            const mappedTrainers = orientationData.trainerData.map(t => ({
              _id: t._id || t.id,
              id: t._id || t.id,
              name: t.name || `${t.firstName || ''} ${t.lastName || ''}`.trim(),
              email: t.email || t.officialEmail,
              department: t.department || (t.employmentInfo?.department)
            }));
            setSelectedTrainerData(mappedTrainers);
          } else if (orientationData.trainer) {
            // Fallback: Auto-search and select trainer only if we don't have object data
            // And only if it's a single trainer or string, to avoid breaking search with comma lists
            const trainerList = Array.isArray(orientationData.trainer) ? orientationData.trainer : [orientationData.trainer];
            if (trainerList.length === 1) {
              setTrainerSearchTerm(String(trainerList[0] || ''));
            }
          }

          // Auto-select location if in-person
          if (orientationData.type === 'in_person' && orientationData.location) {
            // Location will be auto-selected when locations load
          }
        } else {
          showError('Orientation not found');
        }
      } catch (error) {
        showError('Error loading orientation');
      } finally {
        setIsLoadingOrientation(false);
      }
    };

    loadOrientationForEdit();
  }, [id]);

  // Debounce trainer search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTrainerSearch(trainerSearchTerm);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [trainerSearchTerm]);

  // React Query: Fetch default trainers (HR managers, HR executives, and employees)
  const { data: defaultEmployeesData = [], isLoading: isLoadingDefaultEmployees } = useQuery({
    queryKey: ['orientation-trainer-employees-default'],
    queryFn: async () => {
      try {
        const result = await fieldSalesService.getSalesReps({
          limit: 20,
          role: 'hr_manager,hr_executive,employee'
        });

        if (result.success) {
          return result.data;
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    staleTime: 0,
    retry: 1
  });

  // React Query: Search Employees (only fetch on search)
  const { data: searchedEmployeesData, isLoading: isLoadingEmployeeSearch } = useQuery({
    queryKey: ['orientation-trainer-employees-search', debouncedTrainerSearch],
    queryFn: async () => {
      if (!debouncedTrainerSearch || typeof debouncedTrainerSearch !== 'string' || !debouncedTrainerSearch.trim()) {
        return [];
      }

      try {
        const result = await fieldSalesService.getSalesReps({
          search: debouncedTrainerSearch.trim(),
          role: 'hr_manager,hr_executive,employee'
        });

        if (result.success) {
          return result.data;
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    enabled: !!debouncedTrainerSearch && typeof debouncedTrainerSearch === 'string' && debouncedTrainerSearch.trim().length > 0,
    staleTime: 0,
  });

  // Get employees to display in dropdown
  const dropdownEmployees = debouncedTrainerSearch && typeof debouncedTrainerSearch === 'string' && debouncedTrainerSearch.trim()
    ? (searchedEmployeesData || [])
    : defaultEmployeesData;

  // Auto-select trainers when search results are available
  useEffect(() => {
    if (Array.isArray(formData.trainer) && formData.trainer.length > 0 && (!selectedTrainerData || selectedTrainerData.length === 0)) {
      const trainerIdents = formData.trainer.map(t => String(t || '').trim().toLowerCase());
      const employeesToSearch = searchedEmployeesData || dropdownEmployees || [];

      if (employeesToSearch.length > 0) {
        const matchedEmployees = employeesToSearch.filter(emp => {
          const fullName = (emp.name || '').toLowerCase();
          return trainerIdents.includes(fullName) ||
            trainerIdents.includes(String(emp.employeeId || '').toLowerCase()) ||
            trainerIdents.includes(String(emp._id || emp.id || '').toLowerCase());
        });

        if (matchedEmployees.length > 0) {
          setSelectedTrainerData(matchedEmployees);
        }
      }
    }
  }, [id, formData.trainer, selectedTrainerData, dropdownEmployees, searchedEmployeesData]);

  // Close trainer dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      try {
        if (showTrainerDropdown && trainerDropdownRef.current && !trainerDropdownRef.current.contains(event.target)) {
          setShowTrainerDropdown(false);
          // Trigger validation on close
          const trainerError = validateField('trainer', formData.trainer);
          setValidationErrors(prev => ({ ...prev, trainer: trainerError }));
        }
      } catch (_) { }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTrainerDropdown, formData.trainer]);

  // Close department dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDepartmentDropdown && departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target)) {
        setShowDepartmentDropdown(false);
        // Trigger validation on close
        const error = validateField('department', formData.department);
        setValidationErrors(prev => ({ ...prev, department: error }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showDepartmentDropdown, formData.department]);

  // Fetch locations using React Query
  const { data: locationsData, isLoading: loadingLocations } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: async () => {
      try {
        const baseUrl = API_BASE_URL;
        const url = `${baseUrl}/locations?isActive=all&limit=1000`;
        const response = await authFetch(url, {
        credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            }
        });

        if (response.ok) {
          const data = await response.json();

          // Handle different response structures
          if (data.responseCode === 200 && data.data) {
            // Check if data.data is an array or has locations property
            if (Array.isArray(data.data)) {
              return data.data;
            } else if (data.data.locations && Array.isArray(data.data.locations)) {
              return data.data.locations;
            } else if (data.data.data && Array.isArray(data.data.data)) {
              return data.data.data;
            }
          } else if (data.success && data.data) {
            if (Array.isArray(data.data)) {
              return data.data;
            } else if (data.data.locations && Array.isArray(data.data.locations)) {
              return data.data.locations;
            }
          } else if (Array.isArray(data)) {
            return data;
          }
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    staleTime: 0,
    retry: 2,
  });

  const locations = locationsData || [];

  // Auto-select location when locations and formData are ready
  useEffect(() => {
    if (locations.length > 0 && formData.type === 'in_person' && formData.location && !selectedLocationData && id) {
      let locationId = null;
      let locationName = '';

      if (typeof formData.location === 'object' && formData.location !== null) {
        locationId = formData.location._id || formData.location.id;
        locationName = formData.location.name || '';
      } else if (typeof formData.location === 'string') {
        locationName = formData.location.includes(' - ')
          ? formData.location.split(' - ')[0].trim()
          : formData.location.trim();
      }

      const matchedLocation = locations.find(loc => {
        if (locationId) return (loc._id || loc.id) === locationId;
        const locName = loc.name || loc.locationName || loc.label || '';
        return locName.toLowerCase() === locationName.toLowerCase() || locName === locationName;
      });

      if (matchedLocation) {
        setSelectedLocationData(matchedLocation);
        // Ensure formData.location is the full object
        setFormData(prev => ({ ...prev, location: matchedLocation }));
      }
    }
  }, [locations, formData.type, formData.location, selectedLocationData, id]);

  // Validation rules
  const validationRules = {
    title: {
      required: true,
      minLength: 3,
      maxLength: 100,
      pattern: /^[a-zA-Z0-9\s,]+$/,
      message: 'Please enter title'
    },
    description: {
      required: true,
      minLength: 10,
      maxLength: 1000,
      pattern: /^[a-zA-Z0-9\s,]+$/,
      message: 'Description must be 10-1000 characters, alphanumeric with spaces and commas only'
    },
    type: {
      required: true,
      message: 'Please select an orientation type'
    },
    location: {
      required: true,
      message: 'Please select a location'
    },
    trainer: {
      required: true,
      message: 'Please select at least one trainer'
    },
    department: {
      required: true,
      message: 'Please select a department'
    },
    date: {
      required: true,
      message: 'Please select a date'
    },
    time: {
      required: true,
      message: 'Please select a time'
    },
    duration: {
      required: true,
      min: 0.5,
      max: 8,
      message: 'Duration must be between 0.5-8 hours'
    },
    capacity: {
      required: true,
      min: 0,
      max: 200,
      message: 'Capacity must be between 0-200 people'
    }
  };

  // Validate individual field
  const validateField = (field, value) => {
    const rule = validationRules[field];
    if (!rule) return null;

    // Required check
    if (rule.required) {
      if (Array.isArray(value)) {
        if (value.length === 0) return rule.message || `${field} is required`;
      } else if (value === undefined || value === null) {
        return rule.message || `${field} is required`;
      } else if (typeof value === 'string' && value.trim() === '') {
        return rule.message || `${field} is required`;
      } else if (typeof value === 'number' && isNaN(value)) {
        return rule.message || `${field} is required`;
      }
    }

    // Skip other validations if field is empty and not required
    if (!value || (Array.isArray(value) && value.length === 0) || value.toString().trim() === '') return null;

    // Special validation for location field based on orientation type
    if (field === 'location') {
      // For virtual orientations, allow all characters except < and >
      if (formData.type === 'virtual') {
        // Ensure value is a string before string operations
        const strValue = String(value || '');

        // Check for forbidden characters
        if (strValue.includes('<') || strValue.includes('>')) {
          return 'Meeting link cannot contain < or > characters';
        }
        // Check length for URLs
        if (strValue.length < 10) {
          return 'Meeting link must be at least 10 characters';
        }
        if (strValue.length > 200) {
          return 'Meeting link must not exceed 200 characters';
        }
      }
      // For in-person locations (dropdown), only required validation is needed
      // No need for length or pattern validation
      return null;
    }

    // Length validations (skip for location field)
    if (field !== 'location' && rule.minLength && value.length < rule.minLength) {
      return `${field} must be at least ${rule.minLength} characters`;
    }
    if (field !== 'location' && rule.maxLength && value.length > rule.maxLength) {
      return `${field} must not exceed ${rule.maxLength} characters`;
    }

    // Numeric validations
    if (rule.min !== undefined && value < rule.min) {
      return `${field} must be at least ${rule.min}`;
    }
    if (rule.max !== undefined && value > rule.max) {
      return `${field} must not exceed ${rule.max}`;
    }

    // Pattern validation (skip for location field)
    if (field !== 'location' && rule.pattern && !rule.pattern.test(value)) {
      return rule.message || `${field} format is invalid`;
    }

    return null;
  };

  // Validate date (prevent past dates)
  const validateDate = (date) => {
    if (!date) return 'Date is required';

    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      return 'Orientation date cannot be in the past. Please select a future date.';
    }
    if (selectedDate > maxFutureDate) {
      return 'Orientation date cannot be more than 1 year from today.';
    }

    return null;
  };

  // Validate time
  const validateTime = (time) => {
    if (!time) return 'Time is required';

    const [hours, minutes] = time.split(':').map(Number);
    const currentTime = new Date();
    const currentHours = currentTime.getHours();
    const currentMinutes = currentTime.getMinutes();

    // If date is today, check if time is in the future
    if (formData.date === new Date().toISOString().split('T')[0]) {
      if (hours < currentHours || (hours === currentHours && minutes <= currentMinutes)) {
        return 'Orientation time cannot be in the past. Please select a future time.';
      }
    }

    return null;
  };

  // Validate agenda durations against total duration
  const validateAgendaDurations = (duration = formData.duration, agenda = formData.agenda) => {
    if (!duration || agenda.length === 0) {
      return null;
    }

    const totalDurationMinutes = duration * 60; // Convert hours to minutes
    const totalAgendaMinutes = agenda.reduce((sum, item) => {
      return sum + (parseInt(item.duration) || 0);
    }, 0);

    if (totalAgendaMinutes > totalDurationMinutes) {
      return `Total agenda duration (${totalAgendaMinutes} minutes) exceeds orientation duration (${duration} hours = ${totalDurationMinutes} minutes). Please reduce agenda durations.`;
    }

    return null;
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};

    // Validate all fields
    Object.keys(validationRules).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    // Special date validation
    const dateError = validateDate(formData.date);
    if (dateError) {
      newErrors.date = dateError;
    }

    // Special time validation
    const timeError = validateTime(formData.time);
    if (timeError) {
      newErrors.time = timeError;
    }

    // Validate agenda durations
    const agendaError = validateAgendaDurations();
    if (agendaError) {
      newErrors.agenda = agendaError;
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Enforce max lengths and pattern validation at input time for better UX
    let newValue = value;

    // Text field validation - only allow alphanumeric, spaces, and commas
    if (field === 'title' && typeof newValue === 'string') {
      // Remove invalid characters (only allow alphanumeric, spaces, commas)
      newValue = newValue.replace(/[^a-zA-Z0-9\s,]/g, '');
      newValue = newValue.slice(0, validationRules.title.maxLength);
    }
    if (field === 'description' && typeof newValue === 'string') {
      // Remove invalid characters (only allow alphanumeric, spaces, commas)
      newValue = newValue.replace(/[^a-zA-Z0-9\s,]/g, '');
      newValue = newValue.slice(0, validationRules.description.maxLength);
    }
    // Trainer field is now handled by employee dropdown, no need for input sanitization
    // Location field is now handled by dropdown for in-person, only sanitize for virtual
    if (field === 'location' && typeof newValue === 'string') {
      if (formData.type === 'virtual') {
        // For virtual, allow URL format but remove < and > characters
        newValue = newValue.replace(/[<>]/g, '');
        newValue = newValue.slice(0, 200);
      }
      // For in-person locations (dropdown), no sanitization needed
    }

    // Number field validation - only allow positive numbers (0 and greater)
    if (field === 'duration' && typeof newValue === 'number') {
      if (newValue < 0) newValue = 0;
      if (newValue > 8) newValue = 8;
    }
    if (field === 'capacity' && typeof newValue === 'number') {
      if (newValue < 0) newValue = 0;
      if (newValue > 200) newValue = 200;
    }

    setFormData(prev => ({
      ...prev,
      [field]: newValue
    }));

    // If type is changing, re-validate location field with new validation rules
    if (field === 'type') {
      // Clear selected location data when switching to virtual
      if (value === 'virtual') {
        setSelectedLocationData(null);
      }
      // Clear location validation error and re-validate if location has a value
      if (formData.location) {
        setTimeout(() => {
          const locationError = validateField('location', formData.location);
          if (locationError) {
            setValidationErrors(prev => ({
              ...prev,
              location: locationError
            }));
          } else {
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.location;
              return newErrors;
            });
          }
        }, 0);
      }
    }

    // If duration is changing, re-validate agenda durations
    if (field === 'duration') {
      const newDuration = typeof value === 'number' ? value : Number(value) || 0;
      setTimeout(() => {
        const agendaError = validateAgendaDurations(newDuration, formData.agenda);
        if (agendaError) {
          setValidationErrors(prev => ({
            ...prev,
            agenda: agendaError
          }));
        } else {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.agenda;
            return newErrors;
          });
        }
      }, 0);
    }
  };

  // Helper function to filter input to only alphanumeric, space, and comma with character limit
  const filterAlphanumericSpaceComma = (value, maxLength = Infinity) => {
    const filtered = value.replace(/[^a-zA-Z0-9\s,]/g, '');
    return maxLength !== Infinity ? filtered.slice(0, maxLength) : filtered;
  };

  const handleAgendaChange = (id, field, value) => {
    setFormData(prev => {
      const updatedAgenda = prev.agenda.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      );

      const updatedFormData = { ...prev, agenda: updatedAgenda };

      // Validate agenda durations after update
      const agendaError = validateAgendaDurations(prev.duration, updatedAgenda);
      if (agendaError) {
        setValidationErrors(prevErrors => ({
          ...prevErrors,
          agenda: agendaError
        }));
      } else {
        setValidationErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors.agenda;
          return newErrors;
        });
      }

      return updatedFormData;
    });
  };

  const addAgendaItem = () => {
    const newId = agendaIdCounterRef.current++;
    setFormData(prev => ({
      ...prev,
      agenda: [{ id: newId, title: '', duration: 0, description: '' }, ...prev.agenda]
    }));
  };

  const removeAgendaItem = (id) => {
    setFormData(prev => {
      const updatedAgenda = prev.agenda.filter(item => item.id !== id);

      // Validate agenda durations after removal
      const agendaError = validateAgendaDurations(prev.duration, updatedAgenda);
      if (agendaError) {
        setValidationErrors(prevErrors => ({
          ...prevErrors,
          agenda: agendaError
        }));
      } else {
        setValidationErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors.agenda;
          return newErrors;
        });
      }

      return {
        ...prev,
        agenda: updatedAgenda
      };
    });
  };

  // Create or Update orientation mutation using React Query
  const createOrientationMutation = useMutation({
    mutationFn: async (apiData) => {
      if (id) {
        return await orientationService.updateOrientation(id, apiData);
      } else {
        return await orientationService.createOrientation(apiData);
      }
    },
    onSuccess: (result) => {
      if (id) {
        // Update mode
        if (result && result.responseCode === 200) {
          showSuccess('Orientation updated successfully');
          navigate('/orientation');
        } else {
          showError(result?.responseMessage || 'Failed to update orientation');
        }
      } else {
        // Create mode
        if (result && result.responseCode === 201) {
          showSuccess('Orientation created successfully');
          navigate('/orientation');
        } else if (result && result.responseCode === 409) {
          showError(result?.responseMessage || 'An orientation already exists at the same date, time, and location. Please choose different details.');
        } else {
          showError(result?.responseMessage || 'Failed to create orientation');
        }
      }
    },
    onError: (error) => {
      showError(`An error occurred while ${id ? 'updating' : 'creating'} the orientation`);
    },
  });

  // Check if there are any validation errors
  const hasValidationErrors = () => {
    // Check if there are any validation errors in the validationErrors object
    if (Object.keys(validationErrors).length > 0) {
      return true;
    }

    // Check if any agenda items have errors
    const hasAgendaErrors = formData.agenda.some(item => {
      // Title is required
      if (!item.title || item.title.trim() === '') {
        return true;
      }
      // Duration must be at least 5 minutes
      if (!item.duration || item.duration < 5) {
        return true;
      }
      return false;
    });

    return hasAgendaErrors;
  };

  const handleSubmit = async () => {
    // Prevent multiple submissions
    if (isSubmitting || createOrientationMutation.isPending) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Validate form
      if (!validateForm()) {
        showError('Please fix the validation errors before submitting. All required fields must be filled correctly.');
        setIsSubmitting(false);
        return;
      }

      // Check if all required fields are filled
      const requiredFields = ['title', 'description', 'location', 'trainer', 'department', 'date', 'time', 'duration', 'capacity'];
      const missingFields = requiredFields.filter(field => {
        if (field === 'duration' || field === 'capacity') {
          return formData[field] === '' || formData[field] === null || formData[field] === undefined;
        }
        if (typeof formData[field] === 'object' && formData[field] !== null) {
          return false; // Objects are considered filled
        }
        return !formData[field] || formData[field].toString().trim() === '';
      });

      if (missingFields.length > 0) {
        showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        setIsSubmitting(false);
        return;
      }

      // Prepare location data based on type
      let locationData = formData.location;

      if (formData.type === 'in_person' && selectedLocationData) {
        // Construct the structured location object to match LocationManagement/JobApplication pattern
        const fullAddress = selectedLocationData.fullAddress ||
          [selectedLocationData.address?.street, selectedLocationData.address?.city?.name, selectedLocationData.address?.state?.name, selectedLocationData.country?.name, selectedLocationData.address?.postalCode]
            .filter(Boolean).join(', ');

        locationData = {
          id: selectedLocationData._id || selectedLocationData.id,
          name: selectedLocationData.name || '',
          code: selectedLocationData.code || '',
          country: selectedLocationData.country || {
            name: selectedLocationData.address?.country?.name || '',
            isoCode: selectedLocationData.address?.country?.isoCode || ''
          },
          address: {
            street: selectedLocationData.address?.street || '',
            city: selectedLocationData.address?.city || { name: selectedLocationData.address?.city || '' },
            state: selectedLocationData.address?.state || { name: selectedLocationData.address?.state || '', isoCode: selectedLocationData.address?.state_code || '' },
            postalCode: selectedLocationData.address?.postalCode || '',
            fullAddress: fullAddress
          },
          coordinates: selectedLocationData.coordinates || { latitude: null, longitude: null },
          fullAddress: fullAddress
        };
      }

      const apiData = {
        ...formData,
        location: locationData,
        agenda: formData.agenda.map(item => ({
          title: item.title,
          duration: item.duration,
          description: item.description
        }))
      };

      // Use mutation to create orientation
      await createOrientationMutation.mutateAsync(apiData);
    } catch (error) {
      showError('An error occurred while creating the orientation');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canCreateOrientation()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to create orientation sessions.</p>
        </div>
      </div>
    );
  }

  if (isLoadingOrientation && id) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orientation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <PermissionGate permission="onboarding" action="read">
            <Button
              variant="outline"
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </PermissionGate>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{id ? 'Edit Orientation Session' : 'Create Orientation Session'}</h1>
            <p className="text-gray-600 mt-1">{id ? 'Edit orientation session details' : 'Schedule a new employee orientation session'}</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <form className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    onBlur={(e) => {
                      const error = validateField('title', e.target.value);
                      setValidationErrors(prev => ({ ...prev, title: error }));
                    }}
                    onKeyPress={(e) => {
                      // Only allow alphanumeric, spaces, and commas
                      if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    maxLength={validationRules.title.maxLength}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.title ? 'border-red-300' : 'border-gray-300'
                      } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="e.g., New Employee Orientation Batch 1"
                    disabled={isSubmitting}
                    required
                  />
                  <div className="mt-1 flex justify-between items-center">
                    <div>
                      {validationErrors.title && (
                        <p className="text-sm text-red-600">{validationErrors.title}</p>
                      )}
                    </div>
                    <p className={`text-xs ${formData.title.length > (validationRules.title.maxLength * 0.9)
                      ? 'text-red-600'
                      : 'text-gray-500'
                      }`}>
                      {formData.title.length}/{validationRules.title.maxLength} characters
                    </p>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description <span className="text-red-500">*</span></label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    onBlur={(e) => {
                      const error = validateField('description', e.target.value);
                      setValidationErrors(prev => ({ ...prev, description: error }));
                    }}
                    onKeyPress={(e) => {
                      // Only allow alphanumeric, spaces, and commas
                      if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    rows={6}
                    maxLength={validationRules.description.maxLength}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.description ? 'border-red-300' : 'border-gray-300'
                      } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Brief description of the orientation session..."
                    disabled={isSubmitting}
                    required
                  />
                  <div className="mt-1 flex justify-between items-center">
                    <div>
                      {validationErrors.description && (
                        <p className="text-sm text-red-600">{validationErrors.description}</p>
                      )}
                    </div>
                    <p className={`text-xs ${formData.description.length > validationRules.description.maxLength * 0.9
                      ? 'text-red-600'
                      : 'text-gray-500'
                      }`}>
                      {formData.description.length}/{validationRules.description.maxLength} characters
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type <span className="text-red-500">*</span></label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    onBlur={(e) => {
                      const error = validateField('type', e.target.value);
                      setValidationErrors(prev => ({ ...prev, type: error }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.type ? 'border-red-300' : 'border-gray-300'
                      } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="in_person">In-Person</option>
                    <option value="virtual">Virtual</option>
                    {/* <option value="hybrid">Hybrid</option> */}
                  </select>
                  {validationErrors.type && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.type}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.type === 'virtual' ? 'Meeting Link ' : 'Location '}
                    <span className="text-red-500">*</span>
                  </label>
                  {formData.type === 'virtual' ? (
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      onKeyPress={(e) => {
                        // Allow all characters except < and >
                        if (e.key === '<' || e.key === '>') {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        // Remove < and > from pasted text
                        const pastedText = e.clipboardData.getData('text');
                        const cleanedText = pastedText.replace(/[<>]/g, '');
                        if (pastedText !== cleanedText) {
                          e.preventDefault();
                          const newValue = formData.location.slice(0, e.target.selectionStart) + cleanedText + formData.location.slice(e.target.selectionEnd);
                          handleInputChange('location', newValue);
                        }
                      }}
                      onBlur={(e) => {
                        const error = validateField('location', e.target.value);
                        setValidationErrors(prev => ({ ...prev, location: error }));
                      }}
                      maxLength={200}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.location ? 'border-red-300' : 'border-gray-300'
                        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder="https://zoom.us/j/123456789 or https://teams.microsoft.com/l/meetup-join/..."
                      disabled={isSubmitting}
                      required
                    />
                  ) : (
                    <select
                      value={selectedLocationData ? (selectedLocationData._id || selectedLocationData.id) : ''}
                      onChange={(e) => {
                        const selectedLocationId = e.target.value;
                        const selectedLocation = locations.find(loc => (loc._id || loc.id) === selectedLocationId);

                        if (selectedLocation) {
                          setSelectedLocationData(selectedLocation);
                          // Store the whole location object in formData
                          handleInputChange('location', selectedLocation);
                        } else {
                          setSelectedLocationData(null);
                          handleInputChange('location', null);
                        }
                      }}
                      onBlur={(e) => {
                        const error = validateField('location', formData.location);
                        setValidationErrors(prev => ({ ...prev, location: error }));
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.location ? 'border-red-300' : 'border-gray-300'
                        } ${isSubmitting || loadingLocations ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      disabled={isSubmitting || loadingLocations}
                      required
                    >
                      <option value="">
                        {loadingLocations ? 'Loading locations...' : 'Select Location'}
                      </option>
                      {locations.map((location) => (
                        <option key={location._id || location.id} value={location._id || location.id}>
                          {location.name || location.locationName || location.label || 'N/A'}
                        </option>
                      ))}
                    </select>
                  )}
                  {validationErrors.location && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.location}</p>
                  )}
                  {formData.type === 'virtual' && !validationErrors.location && (
                    <p className="mt-1 text-sm text-gray-500">Enter the meeting link (Zoom, Teams, Google Meet, etc.)</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={formData.date}
                    onClick={(e) => e.target.showPicker()}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    onBlur={(e) => {
                      const error = validateDate(e.target.value);
                      setValidationErrors(prev => ({ ...prev, date: error }));
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    max={maxFutureDate.toISOString().split('T')[0]}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent cursor-pointer ${validationErrors.date ? 'border-red-300' : 'border-gray-300'
                      } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting}
                    required
                  />
                  {validationErrors.date && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.date}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time <span className="text-red-500">*</span></label>
                  <input
                    type="time"
                    value={formData.time}
                    onClick={(e) => e.target.showPicker()}
                    onChange={(e) => handleInputChange('time', e.target.value)}
                    onBlur={(e) => {
                      const error = validateTime(e.target.value);
                      setValidationErrors(prev => ({ ...prev, time: error }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent cursor-pointer ${validationErrors.time ? 'border-red-300' : 'border-gray-300'
                      } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting}
                    required
                  />
                  {validationErrors.time && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.time}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duration (hours) <span className="text-red-500">*</span></label>
                  <select
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', Number(e.target.value))}
                    onBlur={(e) => {
                      const error = validateField('duration', Number(e.target.value));
                      setValidationErrors(prev => ({ ...prev, duration: error }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.duration ? 'border-red-300' : 'border-gray-300'
                      } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting}
                    required
                  >
                    <option value="">Select Duration</option>
                    <option value="0.5">30 min</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((hour) => (
                      <option key={hour} value={hour}>
                        {hour} {hour === 1 ? 'hour' : 'hours'}
                      </option>
                    ))}
                  </select>
                  {validationErrors.duration && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.duration}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Capacity <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min="0"
                    max="200"
                    step="1"
                    value={formData.capacity}
                    onBlur={(e) => {
                      const error = validateField('capacity', parseInt(e.target.value));
                      setValidationErrors(prev => ({ ...prev, capacity: error }));
                    }}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      if (val >= 0 && val <= 200) {
                        handleInputChange('capacity', val);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Prevent negative sign and decimal point
                      if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
                        e.preventDefault();
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.capacity ? 'border-red-300' : 'border-gray-300'
                      } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting}
                    required
                  />
                  {validationErrors.capacity && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.capacity}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Enter a value between 0 and 200</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trainer <span className="text-red-500">*</span></label>
                  <div className="relative trainer-dropdown" ref={trainerDropdownRef}>
                    {/* Display selected trainer or placeholder */}
                    <div
                      onClick={() => {
                        setShowTrainerDropdown(!showTrainerDropdown);
                        if (!showTrainerDropdown) {
                          setTrainerSearchTerm("");
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:border-agrosaf-green focus-within:ring-1 focus-within:ring-agrosaf-green ${validationErrors.trainer ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <span className={`flex-1 min-w-0 ${formData.trainer && formData.trainer.length > 0 ? "text-gray-900" : "text-gray-500"}`}>
                        <span className="block truncate">
                          {formData.trainer && formData.trainer.length > 0
                            ? `${formData.trainer.length} trainer(s) selected`
                            : "Search and select trainers..."}
                        </span>
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform ${showTrainerDropdown ? 'transform rotate-180' : ''
                          }`}
                      />
                    </div>

                    {/* Dropdown with search */}
                    {showTrainerDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search by employee name..."
                              value={trainerSearchTerm}
                              onChange={(e) => {
                                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                                const value = e.target.value;
                                const filteredValue = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 150);
                                setTrainerSearchTerm(filteredValue);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              maxLength={150}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="mt-1 text-xs text-gray-500 text-right">
                            {trainerSearchTerm.length}/150 characters
                          </div>
                        </div>

                        {/* Results list */}
                        <div className="max-h-64 overflow-y-auto">
                          {((debouncedTrainerSearch.trim() && isLoadingEmployeeSearch) || (!debouncedTrainerSearch.trim() && isLoadingDefaultEmployees)) ? (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              Searching...
                            </div>
                          ) : dropdownEmployees.length > 0 ? (
                            dropdownEmployees.map((employee) => {
                              const employeeName = employee.name || 'Unknown';
                              const employeeEmail = employee.officialEmail || employee.email || employee.personalEmail || 'N/A';
                              const employeeDepartment = employee.department || 'Unknown Department';
                              const employeeRole = employee.role || 'N/A';
                              const isSelected = Array.isArray(formData.trainer) && (formData.trainer.includes(employee.employeeId) || formData.trainer.includes(employeeName));

                              return (
                                <div
                                  key={employee._id || employee.id}
                                  onClick={() => {
                                    const trainerId = employee.employeeId;
                                    const currentTrainers = Array.isArray(formData.trainer) ? formData.trainer : [];
                                    const isCurrentlySelected = currentTrainers.includes(trainerId) || currentTrainers.includes(employeeName);

                                    const updatedTrainers = isCurrentlySelected
                                      ? currentTrainers.filter(t => t !== trainerId && t !== employeeName)
                                      : [...currentTrainers, trainerId];

                                    setFormData(prev => ({ ...prev, trainer: updatedTrainers }));

                                    setSelectedTrainerData(prev => {
                                      const currentData = Array.isArray(prev) ? prev : [];
                                      const exists = currentData.some(d => (d._id || d.id || d.employeeId) === (employee._id || employee.id || employee.employeeId));
                                      const newData = exists
                                        ? currentData.filter(d => (d._id || d.id || d.employeeId) !== (employee._id || employee.id || employee.employeeId))
                                        : [...currentData, employee];
                                      return newData;
                                    });

                                    setValidationErrors(prev => {
                                      const newErrors = { ...prev };
                                      const trainerError = validateField('trainer', updatedTrainers);
                                      if (trainerError) {
                                        newErrors.trainer = trainerError;
                                      } else {
                                        delete newErrors.trainer;
                                      }
                                      return newErrors;
                                    });
                                  }}
                                  className={`px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 flex items-center justify-between ${isSelected ? "bg-blue-50" : ""
                                    }`}
                                >
                                  <div className="flex-1 min-w-0 mr-2">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                      {employeeName}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                      {employeeEmail}
                                    </div>
                                    <div className="text-xs text-gray-400 truncate">
                                      {employee.employeeId || ""} - {employeeDepartment} - {employeeRole.replace(/_/g, ' ')}
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                      <Check className="h-5 w-5 text-agrosaf-green" />
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              {debouncedTrainerSearch.trim()
                                ? "No employees found"
                                : "Type to search employees..."}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {showTrainerDropdown && debouncedTrainerSearch.trim()
                      ? `Found ${dropdownEmployees.length} employee(s)`
                      : showTrainerDropdown
                        ? "Type to search employees..."
                        : ""}
                  </div>
                  {Array.isArray(selectedTrainerData) && selectedTrainerData.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {selectedTrainerData.map((trainer) => (
                        <div key={trainer._id || trainer.id} className="p-2 bg-blue-50 rounded-lg flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-blue-900 break-words whitespace-normal">
                              {trainer.name || "Unknown"}
                            </div>
                            <div className="text-xs text-blue-700 break-words whitespace-normal">
                              {trainer.officialEmail || trainer.email || trainer.personalEmail || "N/A"}
                            </div>
                            <div className="text-xs text-blue-600 break-words whitespace-normal">
                              {trainer.department || "N/A"} • {(trainer.role || "N/A").replace(/_/g, ' ')}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const trainerId = trainer.employeeId;
                              const trainerName = trainer.name;
                              const currentTrainersList = Array.isArray(formData.trainer) ? formData.trainer : [];
                              const updatedTrainers = currentTrainersList.filter(t => t !== trainerId && t !== trainerName);

                              setFormData(prev => ({
                                ...prev,
                                trainer: updatedTrainers
                              }));
                              setSelectedTrainerData(prev => prev.filter(d => (d._id || d.id || d.employeeId) !== (trainer._id || trainer.id || trainer.employeeId)));

                              // Trigger validation
                              const trainerError = validateField('trainer', updatedTrainers);
                              setValidationErrors(prev => {
                                const newErrors = { ...prev };
                                if (trainerError) {
                                  newErrors.trainer = trainerError;
                                } else {
                                  delete newErrors.trainer;
                                }
                                return newErrors;
                              });
                            }}
                            className="text-blue-400 hover:text-blue-600 ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {validationErrors.trainer && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.trainer}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department <span className="text-red-500">*</span></label>
                  <div className="relative" ref={departmentDropdownRef}>
                    <div
                      onClick={() => {
                        if (!loadingDepartments && !isSubmitting) setShowDepartmentDropdown(!showDepartmentDropdown);
                      }}
                      className={`w-full px-3 py-2 border rounded-md cursor-pointer flex justify-between items-center ${validationErrors.department ? 'border-red-300' : 'border-gray-300'
                        } ${isSubmitting || loadingDepartments ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                    >
                      <span className={`block truncate ${!formData.department ? 'text-gray-500' : 'text-gray-900'}`}>
                        {formData.department || (loadingDepartments ? 'Loading departments...' : 'Select Department')}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDepartmentDropdown ? 'transform rotate-180' : ''}`} />
                    </div>

                    {showDepartmentDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {departments.length > 0 ? (
                          departments.map((dept) => (
                            <div
                              key={dept}
                              className={`px-3 py-2 cursor-pointer whitespace-nowrap hover:bg-blue-50 ${formData.department === dept ? 'bg-blue-50 text-agrosaf-green' : 'text-gray-700'}`}
                              onClick={() => {
                                handleInputChange('department', dept);
                                setShowDepartmentDropdown(false);
                                // validate immediately
                                const error = validateField('department', dept);
                                setValidationErrors(prev => ({ ...prev, department: error }));
                              }}
                              title={dept}
                            >
                              {dept}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-500 whitespace-nowrap">No departments available</div>
                        )}
                      </div>
                    )}
                  </div>
                  {validationErrors.department && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.department}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Agenda */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Agenda</h3>
                <PermissionGate permission="onboarding" action="update">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Plus className="h-4 w-4" />}
                    onClick={addAgendaItem}
                    disabled={isSubmitting || formData.agenda.length >= 15}
                  >
                    Add Item
                  </Button>
                </PermissionGate>
              </div>
              {formData.agenda.length >= 15 && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">Maximum of 15 agenda items allowed. You have reached the limit.</p>
                </div>
              )}
              {validationErrors.agenda && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{validationErrors.agenda}</p>
                </div>
              )}
              <div className="space-y-4">
                {formData.agenda.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium">No agenda items added yet</p>
                    <p className="text-sm">Click "Add Item" to create your orientation agenda</p>
                  </div>
                ) : (
                  formData.agenda.map((item, index) => (
                    <div key={item.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
                        <PermissionGate permission="onboarding" action="update">
                          <button
                            type="button"
                            onClick={() => removeAgendaItem(item.id)}
                            disabled={isSubmitting}
                            className={`text-red-600 hover:text-red-700 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              value={item.title}
                              onChange={(e) => {
                                // Only allow alphanumeric, spaces, and commas
                                const sanitized = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '');
                                handleAgendaChange(item.id, 'title', sanitized);
                              }}
                              onKeyPress={(e) => {
                                // Only allow alphanumeric, spaces, and commas
                                if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              maxLength={100}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${!item.title ? 'border-red-300' : 'border-gray-300'
                                } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                              placeholder="Session title"
                              disabled={isSubmitting}
                              required
                            />
                            <div className="mt-1 flex justify-between items-center">
                              <div>
                                {!item.title && (
                                  <p className="text-sm text-red-600">Title is required</p>
                                )}
                              </div>
                              <p className={`text-xs ${item.title.length > 90
                                ? 'text-red-600'
                                : 'text-gray-500'
                                }`}>
                                {item.title.length}/100 characters
                              </p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min) <span className="text-red-500">*</span></label>
                            <input
                              type="number"
                              min="0"
                              max="120"
                              step="1"
                              value={item.duration}
                              onChange={(e) => {
                                const val = parseInt(e.target.value) || 0;
                                if (val >= 0 && val <= 120) {
                                  handleAgendaChange(item.id, 'duration', val);
                                }
                              }}
                              onKeyDown={(e) => {
                                // Prevent negative sign and decimal point
                                if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
                                  e.preventDefault();
                                }
                              }}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${!item.duration || item.duration < 5 ? 'border-red-300' : 'border-gray-300'
                                } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                              disabled={isSubmitting}
                              required
                            />
                            {(!item.duration || item.duration < 5) && (
                              <p className="mt-1 text-sm text-red-600">Duration must be at least 5 minutes</p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">Enter a value between 0 and 120 minutes</p>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => {
                              // Only allow alphanumeric, spaces, and commas
                              const sanitized = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '');
                              handleAgendaChange(item.id, 'description', sanitized);
                            }}
                            onKeyPress={(e) => {
                              // Only allow alphanumeric, spaces, and commas
                              if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            maxLength={200}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
                              }`}
                            placeholder="Brief description"
                            disabled={isSubmitting}
                          />
                          <div className="mt-1 flex justify-end items-center">
                            <p className={`text-xs ${item.description.length > 180
                              ? 'text-red-600'
                              : 'text-gray-500'
                              }`}>
                              {item.description.length}/200 characters
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <PermissionGate permission="onboarding" action="read">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/orientation')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </PermissionGate>
              <PermissionGate permission={id ? "onboarding" : "onboarding"} action={id ? "update" : "create"}>
                <Button
                  variant="primary"
                  icon={<Send className="h-4 w-4" />}
                  onClick={() => handleSubmit()}
                  disabled={isSubmitting || createOrientationMutation.isPending || isLoadingOrientation}
                  className={`bg-agrosaf-green hover:bg-agrosaf-darkGreen ${isSubmitting || createOrientationMutation.isPending || isLoadingOrientation ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  {isSubmitting || createOrientationMutation.isPending
                    ? (id ? 'Updating...' : 'Creating...')
                    : (id ? 'Update Orientation' : 'Create Orientation')}
                </Button>
              </PermissionGate>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateOrientation;
